import { Form } from "antd"
import { useCallback, useEffect, useMemo } from "react"
import { Alert, Group, Legend, notification, RadioButtons, SelectInput } from "components/common"
import {
  ADD_REVIEW_FORM_RULES,
  ENTITY_OPTIONS,
  REVIEW_TYPE_CONFIG,
  ReviewFormQuestionsSkeleton,
  showValidityField,
} from "../utils"
import { t } from "i18next"
import { convertToReadableString } from "utils"
import { enableOrDisableOptions } from "constants/list"
import { AiOutlineInfoCircle } from "react-icons/ai"
import { useLazyGetReviewFormQuestionsQuery } from "features/leads/api"
import ReviewFormSections from "./ReviewFormSections"
export default function AddReviewFormContent({ form, clientId, leadId, onSubmit, preSelection, entityApiMap }) {
  const { preSelectMeeting, preSelectLead, preSelectCall, callRecord, lead, meeting } = preSelection
  const hasPreSelectedEntity = preSelectMeeting || preSelectCall || preSelectLead
  const reviewType = Form.useWatch("reviewType", form)
  const entityToReview = Form.useWatch("entityToReview", form)
  const isEntityValid = Form.useWatch("isEntityValid", form)
  const entity = Form.useWatch("entity", form)
  const [getReviewFormQuestions, { data: questions, isFetching: isFetchingQuestions }] =
    useLazyGetReviewFormQuestionsQuery()

  const sections = questions?.sections

  const shouldShowQuestionsSkeleton =
    isFetchingQuestions &&
    (entityToReview === "lead" || entityToReview === "client" || showValidityField[entityToReview])

  const shouldShowFormSections =
    sections &&
    Object.keys(sections).length > 0 &&
    (entityToReview === "client" ||
      entityToReview === "lead" ||
      (showValidityField[entityToReview] && isEntityValid !== null && isEntityValid !== undefined))

  const getQuestions = (type, entity, isEntityValidVal) => {
    getReviewFormQuestions(
      {
        reviewType: type,
        entityType: entity,
        isEntityValid: isEntityValidVal,
      },
      true
    )
      .unwrap()
      .then()
      .catch(e => notification?.error({ message: e?.error }))
  }

  const entityConfig = useMemo(() => {
    if (preSelectMeeting) {
      return { type: "meeting", data: meeting }
    }
    if (preSelectCall) {
      return { type: "call", data: callRecord }
    }
    if (preSelectLead) {
      return { type: "lead", data: lead }
    }
    return null
  }, [preSelectMeeting, preSelectCall, preSelectLead, lead, callRecord, meeting])

  useEffect(() => {
    if (!entityConfig) return
    const { type, data } = entityConfig
    const baseValues = {
      reviewType: REVIEW_TYPE_CONFIG()?.[0]?.key,
      entityToReview: type,
    }
    if (data && type !== "meeting") {
      baseValues.entity = data
    }
    form.setFieldsValue(baseValues)
    if (type === "meeting") {
      const apiConfig = entityApiMap?.[type]
      if (!apiConfig?.call) return
      apiConfig
        .call()
        .unwrap()
        .then(response => {
          if (data && data?.id) {
            const list = response?.[apiConfig.dataKey] || []
            const selectedItem = list.find(item => item?.id === data.id)
            if (selectedItem) {
              form.setFieldsValue({ entity: selectedItem })
            }
          }
        })
        .catch(e => {
          notification?.error({ message: e?.error })
        })
    }
  }, [entityConfig, form])

  useEffect(() => {
    if (!preSelectLead || !lead || !reviewType) return
    getQuestions(reviewType, "lead", true)
  }, [preSelectLead, lead, reviewType])

  const handleEntityChange = useCallback(
    e => {
      const selected = e?.target?.value
      const preselectedEntity =
        selected === "lead" && preSelectLead && lead
          ? lead
          : selected === "call" && preSelectCall && callRecord
          ? callRecord
          : selected === "meeting" && preSelectMeeting && meeting
          ? meeting
          : null
      form.setFieldsValue({ entity: preselectedEntity, isEntityValid: null })
      if (selected === "lead" || selected === "client") {
        getQuestions(reviewType, selected, true)
      }
      if (entityApiMap[selected]) {
        entityApiMap[selected].call()
      }
    },
    [entityApiMap, reviewType, form, preSelectLead, preSelectCall, preSelectMeeting, lead, callRecord, meeting]
  )

  return (
    <Form form={form} layout="vertical" onFinish={onSubmit} scrollToFirstError>
      <Group gap="16px" template="1fr">
        <Form.Item
          name="reviewType"
          label={t("Select Review Type")}
          rules={ADD_REVIEW_FORM_RULES().reviewType}
          required
        >
          <RadioButtons
            options={REVIEW_TYPE_CONFIG()}
            getValue={opt => opt?.key}
            disabled={preSelectMeeting && !meeting}
            onChange={() => {
              form.setFieldsValue({
                ...(!hasPreSelectedEntity && { entityToReview: null }),
                isEntityValid: null,
              })
            }}
          />
        </Form.Item>
        {reviewType && (
          <Form.Item
            name="entityToReview"
            label={t("What do you want to review?")}
            rules={ADD_REVIEW_FORM_RULES().entityToReview}
            required
          >
            <RadioButtons
              options={ENTITY_OPTIONS()}
              getValue={opt => opt?.key}
              onChange={handleEntityChange}
              disabled={preSelectMeeting || preSelectCall}
            />
          </Form.Item>
        )}
        {entityToReview && entityApiMap[entityToReview] && (
          <Form.Item
            name="entity"
            label={t(`Select ${convertToReadableString(entityToReview)}`)}
            rules={ADD_REVIEW_FORM_RULES().meetingId}
            required
            hidden={callRecord || meeting || (lead && entityToReview === "lead")}
          >
            <SelectInput
              placeholder={t(`Select ${convertToReadableString(entityToReview)}`)}
              options={entityApiMap[entityToReview]?.data || []}
              getOptionValue={entityApiMap[entityToReview]?.getOptionValue}
              getOptionLabel={entityApiMap[entityToReview]?.getOptionLabel}
              inputLoading={entityApiMap[entityToReview]?.loading}
              onChange={(value, option) => form.setFieldValue("entity", option)}
            />
          </Form.Item>
        )}
        {showValidityField[entityToReview] && (
          <>
            <Form.Item
              name="isEntityValid"
              label={t(`Did the {{entityToReview}} take place`, {
                entityToReview: convertToReadableString(entityToReview),
              })}
              rules={ADD_REVIEW_FORM_RULES().isEntityValid}
              required
              extra={
                <Alert
                  message={t(`Selection will impact the {{entityToReview}} verification status`, { entityToReview })}
                  type="error"
                  icon={<AiOutlineInfoCircle />}
                  showIcon
                  style={{ marginTop: 12 }}
                />
              }
            >
              <RadioButtons
                options={enableOrDisableOptions()}
                getValue={opt => opt?.value}
                onChange={value => {
                  form.setFieldValue("is_entity_valid", value?.target?.value) // need to be updated from BE
                  getQuestions(reviewType, entityToReview, value?.target?.value)
                }}
              />
            </Form.Item>
          </>
        )}
        {shouldShowQuestionsSkeleton && <ReviewFormQuestionsSkeleton />}
        {shouldShowFormSections && (
          <ReviewFormSections sections={sections} form={form} callNextTaskAPI={questions?.callNextProposedTask} />
        )}
      </Group>
    </Form>
  )
}
